'use client';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconChevronDown } from '@repo/svgs';

export interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Header content for the accordion trigger */
  header: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Default open for uncontrolled usage */
  defaultOpen?: boolean;
  /** Open state change callback */
  onOpenChange?: (open: boolean) => void;
  /** Content of the accordion */
  children?: React.ReactNode;
  /** Optional classes */
  headerClassName?: string;
  contentClassName?: string;
  /** When true, clicking outside collapses the accordion (default true) */
  closeOnOutsideClick?: boolean;
  /** Animation duration in ms (default 200) */
  animationDurationMs?: number;
}

export function Accordion({
  header,
  open,
  defaultOpen = false,
  onOpenChange,
  children,
  className,
  headerClassName,
  contentClassName,
  closeOnOutsideClick = true,
  animationDurationMs = 200,
  ...props
}: AccordionProps) {
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen);
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number>(0);

  // Measure content height whenever open changes or content size may change
  useEffect(() => {
    if (!contentRef.current) return;
    const el = contentRef.current;
    // When opening, set to scrollHeight; when closing, set to 0
    const next = isOpen ? el.scrollHeight : 0;
    setHeight(next);
  }, [isOpen, children]);

  // Outside click to close
  useEffect(() => {
    if (!closeOnOutsideClick) return;
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (rootRef.current?.contains(t)) return; // inside
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, closeOnOutsideClick, setOpen]);

  const id = useId();

  return (
    <div ref={rootRef} className={cn('w-full', className)} {...props}>
      <button
        type="button"
        className={cn(
          // layout
          'w-full flex items-center gap-3',
          // spacing
          'px-3 py-3',
          // shape
          'rounded-xl',
          'hover:bg-primary-hover',
          // colors
          isOpen ? 'bg-primary-press ' : 'bg-primary ',
          // text
          'text-body-2 text-primary',
          // cursor
          'cursor-pointer',
          headerClassName,
        )}
        aria-expanded={isOpen}
        aria-controls={`${id}-content`}
        onClick={() => setOpen(!isOpen)}
      >
        <div className="flex-1 text-left min-w-0">{header}</div>
        <IconChevronDown
          className={cn(
            'w-3 h-3 text-secondary transition-transform',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>

      <div
        id={`${id}-content`}
        aria-hidden={!isOpen}
        className={cn(
          // make space and animate
          'overflow-hidden will-change-[height,opacity,transform]',
        )}
        style={{
          height,
          transition: `height ${animationDurationMs}ms ease, opacity ${animationDurationMs}ms ease, transform ${animationDurationMs}ms ease`,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0px)' : 'translateY(-2px)',
        }}
      >
        <div ref={contentRef} className={cn('px-3 pt-2 pb-3', contentClassName)}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default Accordion;
